/**
 * Hero & header presentation helpers.
 *
 * All visual values come from saved admin settings — nothing here is hard-coded
 * except the fallback defaults, which mirror the backend defaults so the page
 * still renders if the API is unreachable.
 */

export const HERO_DEFAULTS = {
  headline: 'Professional Membership Management',
  subheadline:
    'Join our community and enjoy seamless membership management, secure document storage, and transparent payment tracking.',
  primary_cta: { label: 'Apply for Membership', url: '/apply', new_tab: false, show: true },
  secondary_cta: { label: 'Member Login', url: '/login', new_tab: false, show: true },
  image_url: 'https://images.pexels.com/photos/7640434/pexels-photo-7640434.jpeg',
  image_alt: 'Members of the scheme at work',
  focal_x: 50,
  focal_y: 50,
  height: 'standard',
  custom_height_vh: 80,
  content_alignment: 'left',
  overlay_color: '#0B1220',
  overlay_opacity: 85,
  gradient_direction: 'to_right',
  gradient_coverage: 55,
  text_mode: 'light',
  heading_color: '#FFFFFF',
  body_color: '#E5E7EB',
  cta_bg_color: '#1D4ED8',
  cta_text_color: '#FFFFFF',
  cta_radius: 10,
};

export const HEADER_DEFAULTS = {
  get_started_show: true,
  get_started_label: 'Get Started',
  get_started_url: '/apply',
  get_started_new_tab: false,
  get_started_bg_color: '#1D4ED8',
  get_started_text_color: '#FFFFFF',
  get_started_radius: 999,
  login_show: true,
  login_label: 'Login',
  login_url: '/login',
  style_over_hero: 'solid_on_scroll',
};

export const OVERLAY_PRESETS = [
  {
    id: 'dark-left',
    name: 'Dark left',
    values: { overlay_color: '#0B1220', overlay_opacity: 85, gradient_direction: 'to_right', gradient_coverage: 55, content_alignment: 'left', text_mode: 'light', heading_color: '#FFFFFF', body_color: '#E5E7EB' },
  },
  {
    id: 'light-left',
    name: 'Light left',
    values: { overlay_color: '#FFFFFF', overlay_opacity: 88, gradient_direction: 'to_right', gradient_coverage: 50, content_alignment: 'left', text_mode: 'dark', heading_color: '#0F172A', body_color: '#334155' },
  },
  {
    id: 'brand',
    name: 'Brand colour',
    values: { overlay_color: '#1D4ED8', overlay_opacity: 82, gradient_direction: 'to_right', gradient_coverage: 60, content_alignment: 'left', text_mode: 'light', heading_color: '#FFFFFF', body_color: '#E2E8F0' },
  },
  {
    id: 'bottom-fade',
    name: 'Bottom fade',
    values: { overlay_color: '#09090B', overlay_opacity: 80, gradient_direction: 'to_top', gradient_coverage: 70, content_alignment: 'center', text_mode: 'light', heading_color: '#FFFFFF', body_color: '#E5E7EB' },
  },
];

export const HEIGHT_VH = { compact: 55, standard: 72, tall: 90 };

export const ANGLE_CSS = {
  to_right: 'to right',
  to_left: 'to left',
  to_top: 'to top',
  to_bottom: 'to bottom',
};

/** Direction that keeps the solid part of the gradient under the content. */
export const directionForAlignment = (alignment) =>
  ({ left: 'to_right', right: 'to_left', center: 'to_top' }[alignment] || 'to_right');

export const hexToRgbTriplet = (hex) => {
  const clean = String(hex || '#000000').replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return '0 0 0';
  return `${(num >> 16) & 255} ${(num >> 8) & 255} ${num & 255}`;
};

export const heroHeightCss = (hero) =>
  hero.height === 'custom' ? `${hero.custom_height_vh}vh` : `${HEIGHT_VH[hero.height] || 72}vh`;

/**
 * CSS custom properties for the overlay. On narrow screens the gradient flips to
 * vertical so text stays readable over a cropped image.
 */
export const heroCssVars = (hero, { mobile = false } = {}) => {
  const direction = mobile ? 'to_top' : hero.gradient_direction;
  const coverage = mobile ? Math.max(hero.gradient_coverage, 70) : hero.gradient_coverage;
  return {
    '--hero-overlay-color': hexToRgbTriplet(hero.overlay_color),
    '--hero-overlay-opacity': (hero.overlay_opacity ?? 85) / 100,
    '--hero-overlay-angle': ANGLE_CSS[direction] || 'to right',
    '--hero-overlay-coverage': `${coverage}%`,
  };
};

export const HERO_OVERLAY_BACKGROUND =
  'linear-gradient(var(--hero-overlay-angle), ' +
  'rgb(var(--hero-overlay-color) / var(--hero-overlay-opacity)) 0%, ' +
  'rgb(var(--hero-overlay-color) / var(--hero-overlay-opacity)) var(--hero-overlay-coverage), ' +
  'transparent 100%)';

/** Responsive sources, but only for images we serve ourselves. */
export const heroSrcSet = (url, widths = [480, 768, 1200, 1600]) => {
  if (!url || !url.includes('/api/settings/branding-image/')) return undefined;
  const joiner = url.includes('?') ? '&' : '?';
  return widths.map((w) => `${url}${joiner}w=${w} ${w}w`).join(', ');
};

/** Fire-and-forget CTA analytics; never blocks navigation. */
export const trackCtaClick = (location) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/settings/cta-click`;
    const body = JSON.stringify({ location });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch (e) {
    /* analytics must never break the CTA */
  }
};
